/**
 * The live module's own flow-control buttons (manifest admin.actions) and the
 * warning before Seuraava leaves a module with unmet gate checks (admin.checks).
 * Platform components: labels, stages and the result line all come from the
 * module's manifest; nothing here knows a module.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Loader2, Play, AlertTriangle, Check } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useConfirm } from '../DialogProvider.jsx';

const ACTIVE = new Set(['queued', 'running']);

function text(labels, language) {
  return labels?.[language] || labels?.fi || '';
}

/** "{juurisyyt} juurisyytä" filled from the job result; unknown fields stay visible. */
function fill(template, result) {
  return template.replace(/\{(\w+)\}/g, (m, k) => (result && result[k] !== undefined ? String(result[k]) : m));
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid rgba(110, 231, 183, 0.5);
  background: rgba(16, 185, 129, 0.14);
  color: #d1fae5;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: progress; }
`;

const Status = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: ${(p) => (p.$tone === 'error' ? '#fca5a5' : p.$tone === 'ok' ? '#a7f3d0' : '#cbd5e1')};
`;

const spin = { animation: 'spin 1s linear infinite' };

const ParamRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
  font-size: 13px;
  color: #cbd5e1;
  select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.4);
    background: #0f172a;
    color: #e2e8f0;
    font: inherit;
  }
`;

/** An action's run-time choices (manifest params): a select per choice, a checkbox per toggle. */
function ActionParams({ params, values, onChange, disabled }) {
  const { language } = useTranslation();
  return params.map((p) => (
    <ParamRow key={p.key} title={text(p.hints, language)}>
      {p.type === 'toggle' ? (
        <input type="checkbox" checked={Boolean(values[p.key])} disabled={disabled} onChange={(e) => onChange(p.key, e.target.checked)} />
      ) : null}
      {text(p.labels, language)}
      {p.type === 'choice' ? (
        <select value={values[p.key]} disabled={disabled} onChange={(e) => onChange(p.key, e.target.value)}>
          {p.options.map((o) => <option key={o.value} value={o.value} title={text(o.hints, language)}>{text(o.labels, language)}</option>)}
        </select>
      ) : null}
    </ParamRow>
  ));
}

const defaults = (params) => Object.fromEntries((params || []).map((p) => [p.key, p.default]));

function ActionStatus({ action }) {
  const { t, language } = useTranslation();
  const job = action.latestJob;
  if (!job) return <Status>{t('workshopOrchestrator.moduleActions.neverRun')}</Status>;
  if (ACTIVE.has(job.status)) {
    const stage = text(action.stages?.[job.stage], language) || job.stage || '';
    return (
      <Status>
        {t('workshopOrchestrator.moduleActions.running')}
        {stage ? ` · ${stage}` : ''}
        {Number.isFinite(job.progress) ? ` · ${job.progress} %` : ''}
      </Status>
    );
  }
  if (job.status === 'failed') {
    return <Status $tone="error">{t('workshopOrchestrator.moduleActions.failed')}: {job.error || '—'}</Status>;
  }
  const errors = Array.isArray(job.result?.errors) ? job.result.errors.length : 0;
  const summary = action.summary ? fill(text(action.summary, language), job.result) : '';
  return (
    <Status $tone={errors ? 'error' : 'ok'}>
      {t('workshopOrchestrator.moduleActions.done')}
      {summary ? ` · ${summary}` : ''}
      {errors ? ` · ${t('workshopOrchestrator.moduleActions.errors', { count: errors })}` : ''}
    </Status>
  );
}

export function ModuleActionButtons({ actions = [], onRun }) {
  const { language } = useTranslation();
  const confirm = useConfirm();
  const [starting, setStarting] = useState(null);
  const [error, setError] = useState(null);
  // Run-time choices per action, from the manifest defaults until changed.
  const [values, setValues] = useState({});
  if (!actions.length) return null;
  const valuesFor = (a) => ({ ...defaults(a.params), ...(values[a.id] || {}) });
  return (
    <Wrap>
      {actions.map((a) => {
        const busy = starting === a.id || ACTIVE.has(a.latestJob?.status);
        return (
          <div key={a.id}>
            {a.params?.length ? (
              <ActionParams
                params={a.params}
                values={valuesFor(a)}
                disabled={busy}
                onChange={(key, v) => setValues((all) => ({ ...all, [a.id]: { ...valuesFor(a), [key]: v } }))}
              />
            ) : null}
            <ActionButton
              type="button"
              disabled={busy}
              title={text(a.hints, language)}
              onClick={async () => {
                if (a.confirm && !(await confirm({ message: text(a.confirm, language), destructive: true }))) return;
                setStarting(a.id);
                setError(null);
                try { await onRun(a.id, valuesFor(a)); } catch (err) { setError(err.message); } finally { setStarting(null); }
              }}
            >
              {busy ? <Loader2 size={16} style={spin} /> : <Play size={16} />}
              {text(a.labels, language)}
            </ActionButton>
            <ActionStatus action={a} />
          </div>
        );
      })}
      {error ? <Status $tone="error">{error}</Status> : null}
    </Wrap>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(2, 6, 23, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Dialog = styled.div`
  width: min(520px, 100%);
  background: #0f172a;
  border: 1px solid rgba(251, 191, 36, 0.45);
  border-radius: 14px;
  padding: 20px;
  color: #e2e8f0;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 18px;
`;

const Message = styled.p`
  margin: 0 0 10px;
  font-size: 15px;
  line-height: 1.45;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const DialogButton = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? 'rgba(110, 231, 183, 0.6)' : 'rgba(148, 163, 184, 0.4)')};
  background: ${(p) => (p.$primary ? 'rgba(16, 185, 129, 0.2)' : 'transparent')};
  color: ${(p) => (p.$primary ? '#d1fae5' : '#e2e8f0')};
`;

/** The unmet leave checks, with "run it now", "continue anyway" and "cancel". */
export function LeaveCheckDialog({ checks, actions = [], onRunAction, onContinue, onCancel }) {
  const { t, language } = useTranslation();
  const runnable = checks
    .map((c) => actions.find((a) => a.id === c.action))
    .filter(Boolean)
    .filter((a, i, arr) => arr.indexOf(a) === i);
  return (
    <Backdrop onClick={onCancel}>
      <Dialog role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <Title><AlertTriangle size={20} color="#fbbf24" />{t('workshopOrchestrator.moduleActions.leaveTitle')}</Title>
        {checks.map((c) => (
          <Message key={c.id}>
            {text(c.labels, language) || t('workshopOrchestrator.moduleActions.checkUnreadable', { detail: c.detail || '' })}
          </Message>
        ))}
        <Buttons>
          <DialogButton type="button" onClick={onCancel}>{t('workshopOrchestrator.moduleActions.cancel')}</DialogButton>
          <DialogButton type="button" onClick={onContinue}>{t('workshopOrchestrator.moduleActions.continueAnyway')}</DialogButton>
          {runnable.map((a) => (
            <DialogButton key={a.id} type="button" $primary onClick={() => onRunAction(a.id)}>
              <Check size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {text(a.labels, language)}
            </DialogButton>
          ))}
        </Buttons>
      </Dialog>
    </Backdrop>
  );
}
